import * as calc from '@/app/order-calculations';
import { IOrder } from './order-calculations-interface';
import { IOverdueNotice } from './invoice-with-overdue-notice-calculations-interface';
import Decimal from '@/utils/decimal';
import uniqBy from 'lodash/uniqBy';
import { GQL_RoundingTypeEnum } from '@/queries/global';
import { roundAmount } from '@/utils/calculation';
export interface IInvoice {
  taxAmount: Decimal;
  lineExtensionAmount: Decimal;
  taxExclusiveAmount: Decimal;
  taxInclusiveAmount: Decimal;
  payableRoundingAmount: Decimal;
  payableAmount: Decimal;
  orders: IOrder[];
  invoiceOverdueNotices: IOverdueNotice[];
}

export interface IInvoiceTaxSubtotal {
  taxableAmount: Decimal;
  taxPercent: Decimal;
  taxAmount: Decimal;
  taxInclusiveAmount: Decimal;
}

// Invoice calculations

export const invoiceTaxSubtotals = (
  invoice: IInvoice,
): IInvoiceTaxSubtotal[] => {
  const taxPercents = uniqBy(
    invoice.orders.flatMap(order => order.orderLines).map(line => line.taxPercent),
    taxPercent => taxPercent.toString(),
  );

  return taxPercents.map(taxPercent => {
    const lineExtensionAmounts = invoice
      .orders
      .flatMap(order => order.orderLines)
      .filter(line => line.taxPercent.equals(taxPercent))
      .map(line => line.lineExtensionAmount);

    const allowanceChargeAmounts = invoice
      .orders
      .flatMap(order => order.orderAllowanceCharges)
      .flatMap(ac =>
        ac.orderAllowanceChargeLines
          .filter(line => line.taxPercent.equals(taxPercent))
          .map(line => line.amount),
      );

    const taxableAmount = calc.orderTaxSubtotalTaxableAmount(
      lineExtensionAmounts,
      allowanceChargeAmounts,
    );

    const taxAmount = calc.orderTaxSubtotalTaxAmount(
      taxableAmount,
      taxPercent,
    );

    const taxInclusiveAmount = calc.orderTaxSubtotalTaxInclusiveAmount(
      taxableAmount,
      taxAmount,
    );

    return {
      taxPercent,
      taxableAmount,
      taxAmount,
      taxInclusiveAmount,
    };
  });
};

export const invoiceTaxAmount = (
  taxSubtotals: IInvoiceTaxSubtotal[],
) => calc.orderTaxAmount(
  taxSubtotals.map(subtotal => subtotal.taxAmount),
);

export const invoiceLineExtensionAmount = (
  invoice: IInvoice,
) => calc.orderLineExtensionAmount(
  invoice
    .orders
    .flatMap(order => order.orderLines)
    .map(line => line.lineExtensionAmount),
);

export const invoiceTaxExclusiveAmount = (
  invoice: IInvoice,
) => calc.orderTaxExclusiveAmount(
  invoice.lineExtensionAmount,
  invoice
    .orders
    .flatMap(order => order.orderAllowanceCharges)
    .flatMap(
      ac => ac
        .orderAllowanceChargeLines
        .filter(acl => !acl._destroy)
        .map(acl => acl.amount),
    ),
);

export const invoiceTaxInclusiveAmount = (
  invoice: IInvoice,
) => calc.orderTaxInclusiveAmount(
  invoice.taxExclusiveAmount,
  invoice.taxAmount,
);

export const invoiceRoundingAmount = (
  invoice: IInvoice,
  rounding: GQL_RoundingTypeEnum,
) => roundAmount(
  invoice.taxInclusiveAmount,
  rounding,
).minus(invoice.taxInclusiveAmount);

export const invoicePayableAmount = (
  invoice: IInvoice,
) => invoice.taxInclusiveAmount.plus(invoice.payableRoundingAmount);
